import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Truck } from "lucide-react";
import { IMG } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

const NODES = [
  { k: "Bremen", s: "Standort" },
  { k: "Deutschland", s: "Ferntransporte" },
  { k: "Europa", s: "Grenzüberschreitend" },
];

export const RouteBridge = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 90%", "end 40%"] });
  const p = useSpring(scrollYProgress, { stiffness: 80, damping: 22, restDelta: 0.001 });
  const left = useTransform(p, [0, 1], ["0%", "100%"]);
  const lineScale = useTransform(p, [0, 1], [0, 1]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      id="route"
      ref={ref}
      data-testid="route-bridge-section"
      className="relative w-full bg-[#0a0a0a] grain overflow-hidden py-20 sm:py-28 lg:py-36"
    >
      <motion.img
        style={{ y: bgY }}
        src={IMG.truckAutobahn}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-[112%] object-cover opacity-[0.22]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />

      <div className="relative z-10 w-full px-5 sm:px-8 lg:px-12">
        <Reveal>
          <h2 className="font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[clamp(2.2rem,8vw,6rem)] max-w-[1200px]">
            Von Bremen <span className="text-[#f95700]">nach Europa.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-5 max-w-xl text-[15px] sm:text-base text-neutral-400 leading-relaxed">
            Jede Fahrt beginnt bei uns in Bremen – und endet dort, wo Sie sie brauchen.
          </p>
        </Reveal>

        <div className="mt-16 sm:mt-24 relative">
          <div className="relative h-[2px] bg-white/12">
            <motion.div style={{ scaleX: lineScale }} className="absolute inset-0 bg-[#f95700] origin-left" />
            <motion.div
              style={{ left }}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 sm:w-14 sm:h-14 bg-[#f95700] grid place-items-center shadow-[0_0_40px_rgba(249,87,0,0.5)]"
            >
              <Truck size={22} className="text-white" />
            </motion.div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-6">
            {NODES.map((n, i) => (
              <motion.div
                key={n.k}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                className={`flex flex-col ${i === 1 ? "items-center text-center" : i === 2 ? "items-end text-right" : ""}`}
              >
                <span className="w-3 h-3 bg-white rotate-45 -mt-[2.6rem] mb-6" />
                <span className="font-display text-xl sm:text-4xl font-black uppercase text-white leading-none">
                  {n.k}
                </span>
                <span className="font-mono-t text-[10px] sm:text-[11px] uppercase tracking-widest text-[#f95700] mt-2">
                  {n.s}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
